import React from 'react'
import styled from 'styled-components'

import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'
import { TRACKING_SOURCE } from '@hozana/tracking/constants'

import { Block, TBlockProps } from 'elements/layout/Block'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { PreloadDiv } from 'elements/layout/PreloadDiv'
import { Row } from 'elements/layout/Row'
import { Space } from 'elements/layout/Space'
import { StretchCell } from 'elements/layout/StretchCell'
import { H } from 'elements/text/H'

import { CommunityCheckExplanation } from 'modules/community/components/CommunityCheckExplanation'
import { CommunityDates } from 'modules/community/components/CommunityDates'
import { CommunityDraftExplanation } from 'modules/community/components/CommunityDraftExplanation'
import { CommunityMembers } from 'modules/community/components/CommunityMembers'
import { CommunityPrivateBadge } from 'modules/community/components/CommunityPrivateBadge'
import { CommunityRoleBadge } from 'modules/community/components/CommunityRoleBadge'
import { COMMUNITY_STATUS } from 'modules/community/constants'
import { getUserRole } from 'modules/community/functions'
import type { TCommunity } from 'modules/community/types'
import { PUBLICATION_MAX_WIDTH } from 'modules/publication/constants'
import { PreloadUserInfo } from 'modules/user/components/PreloadUserInfo'
import { UserCard } from 'modules/user/components/UserCard'
import { UserProfileLink } from 'modules/user/components/UserProfileLink'

import { CommunityBgImage } from './CommunityBgImage'
import { CommunityFrequencyAlert } from './CommunityFrequencyAlert'
import { CommunitySubscriptionButtons } from './CommunitySubscriptionButtons'

const StyledBlock = styled(Block)<TBlockProps>`
  ${mediaQuery('xs', 'xs')} {
    box-shadow: none;
  }
  ${mediaQuery('xs', 'md')} {
    border-radius: 0;
  }
`

export type TCommunityPageContentProps = {
  community: TCommunity
  tabsBar: JSX.Element
  communityUpToDate?: boolean
}

/**
 * ### CommunityPageTopSection ###
 */
export const CommunityPageTopSection: TFC<TCommunityPageContentProps> = ({ community, tabsBar, communityUpToDate }) => {
  const roles = getUserRole(community)

  return (
    <StyledBlock square noPrint zIndex="zIndex1" maxW="990px" m="auto">
      {/* Community picture */}
      <CommunityBgImage image={community.picture} />
      <Div p="10px 10px 0px" m="auto" maxW={{ xs: 'initial', md: PUBLICATION_MAX_WIDTH, lg: 'initial' }}>
        <Row>
          <Cell align="middleBetween" column flex="1">
            <Row gutter="20px" w="100%">
              {/* Main column in the top block : title, description, ... */}
              <StretchCell align="middleLeft" p={{ xs: '1px', lg: '10px 10px 0 !important' }}>
                <Div w="100%">
                  {/* Title */}
                  <header>
                    <H
                      size="1"
                      fontSize={{ xs: '1.5em', sm: '1.7em', md: '1.9em', lg: '2.2em' }}
                      lineHeight="1.4em"
                      color="darkBlue"
                      align="left"
                      m={{ xs: '0 0 5px', lg: '10px 0 20px' }}
                      data-testid={QA.COMMUNITY_PAGE.TITLE}
                    >
                      {community.name}
                    </H>
                  </header>
                </Div>
              </StretchCell>
              <Cell p="0 10px 10px !important" w="100%">
                {/* Name and roles of the user : creator, administrator, animator */}
                <Div p="5px 0">
                  {!communityUpToDate ? (
                    <PreloadUserInfo />
                  ) : (
                    <>
                      {/* Avatar */}
                      {!roles.isCreator && (
                        <UserProfileLink userId={community.user} w="100%">
                          <UserCard userId={community.user}>trans:community:community.animated-by.short</UserCard>
                        </UserProfileLink>
                      )}
                      {roles.hasRole && <CommunityRoleBadge community={community} fontSize="1em" longText />}
                    </>
                  )}
                </Div>
                {/* Main actions on community page : join / leave... */}
                <Div align="center" w="fit-content" visible={['xs', 'sm', 'md']}>
                  <CommunityMembers community={community} noEllipsis align="center" p="12px" />
                </Div>
                <Div>
                  {!communityUpToDate ? (
                    <PreloadDiv h="45px" />
                  ) : (
                    <>
                      <CommunityFrequencyAlert community={community} />
                      <CommunityDates community={community} />
                    </>
                  )}
                  <CommunityPrivateBadge community={community} />
                </Div>
              </Cell>
            </Row>
            <Div w="100%" visible={['lg', 'xl']}>
              <Div m="0 -10px" data-testid={QA.COMMUNITY_PAGE.TABS_BAR}>
                {tabsBar}
              </Div>
            </Div>
          </Cell>
          <Cell
            w={{ xs: '100%', lg: '260px' }} // 260px corresponds to the sidebar width
            p="10px"
          >
            <Div align="center" visible={['lg', 'xl']} m="0 0 10px 0">
              <CommunityMembers community={community} fontSize="1em" noEllipsis align="center" p="12px" />
            </Div>
            {!communityUpToDate ? (
              <PreloadDiv h="45px" />
            ) : community?.status === COMMUNITY_STATUS.DRAFT ? (
              <CommunityDraftExplanation community={community} />
            ) : community?.status === COMMUNITY_STATUS.CHECK ? (
              <CommunityCheckExplanation />
            ) : (
              <CommunitySubscriptionButtons
                trackingSource={TRACKING_SOURCE.COMMUNITY_TOP_SECTION}
                community={community}
              />
            )}
          </Cell>
        </Row>
      </Div>
      {/* TabsBar */}
      <Div data-testid={QA.COMMUNITY_PAGE.TABS_BAR} visible={['xs', 'sm', 'md']}>
        <Space />
        {tabsBar}
      </Div>
    </StyledBlock>
  )
}
